import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { DotLoader, ValidatedInput } from '../../../components';
import { EditSVG } from '../../../components/svgs';
import { ValidatedState } from '../../../components/custom-input/ValidatedInput';
import TransactionLabelField from '../../../components/transaction/TransactionLabelField';
import { ValidateAddress } from '../TransactionCartWithdraw';
import { ShippingSummary } from '../../../components/insurance-form/InsuranceForm';
import PaymentInformation from '../shipping-payment-info/PaymentInformation';
import { CartTransactionResponse, SpecimenCartItem } from '../../../types/interfaces/transactionCart.interfaces';
import { EmbryoStraw, SpecimenSelection } from '../../../types/interfaces';
import { TransactionTypeEnum, Validators } from '../../../types/enums';
import { withdrawInventory } from '../../../api/inventoryApi';
import { deleteTransactionCartItem } from '../../../api/transactionCartApi';
import { addBulkOrderShippingAddress } from '../../../api/bulkOrderShippingAddressApi';
import { showToast } from '../../../services/toast.service';
import { PermissionService } from '../../../services/permission.service';
import { fetchTransactionCartItems } from '../../../services/transactionCart.service';
import { getShippingNotes } from '../../../utils/addressHelpers';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { toastMessages } from '../../../constants/errorMessages';
import {
  ADDRESS_CONSTANTS,
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_MANAGEMENT_CONSTANTS,
  LABEL_CONSTANTS,
  PAYMENT_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../../constants/common';
import './shippingPreview.scss';

type CustomerInfoForm = {
  customerName: ValidatedState;
  customerEmail: ValidatedState;
  customerPhoneNumber: ValidatedState;
};

type ShippingPreviewProps = {
  withdrawCartItem: CartTransactionResponse | undefined;
  shippingAddress: ValidateAddress | undefined;
  shippingSummary: ShippingSummary | undefined;
  setIsPreview: (isPreview: boolean) => void;
  customerInfo: CustomerInfoForm;
  setCustomerInfo: (customerInfo: CustomerInfoForm) => void;
  breedDate: Date | undefined;
  rentDryVaporShipperTank: string;
  notes: string;
  permissionService: PermissionService;
};

const ShippingPreview = ({
  withdrawCartItem,
  shippingAddress,
  shippingSummary,
  setIsPreview,
  customerInfo,
  setCustomerInfo,
  breedDate,
  rentDryVaporShipperTank,
  notes,
  permissionService,
}: ShippingPreviewProps) => {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const user = useAppSelector(state => state.user);

  const [isBuyerInfoEditMode, setIsEditBuyerInfoMode] = useState<boolean>(false);
  const [customerName, setCustomerName] = useState<ValidatedState>(customerInfo.customerName);
  const [customerEmail, setCustomerEmail] = useState<ValidatedState>(customerInfo.customerEmail);
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState<ValidatedState>(customerInfo.customerPhoneNumber);
  const [isPaymentPage, setIsPaymentPage] = useState<boolean>(false);
  const [withdrawingInventory, setWithdrawingInventory] = useState<boolean>(true);

  const totalDiscardQuantity = withdrawCartItem?.specimenCartItems?.reduce(
    (accumulator: number, specimenCartItem) => accumulator + specimenCartItem?.quantity,
    0,
  );

  const bulkOrderShippingAddress = {
    shipToAddressLine:
      (shippingAddress?.streetAddress?.value ?? '').replace(/,/g, '') +
      ', ' +
      (shippingAddress?.streetAddress2?.value ?? '').replace(/,/g, ''),
    shipToCity: shippingAddress?.city?.value.replace(/,/g, ''),
    shipToState: shippingAddress?.state?.value.replace(/,/g, ''),
    shipToPostalCode: shippingAddress?.postalCode?.value.replace(/,/g, ''),
    shipToCountry: shippingAddress?.country?.value.replace(/,/g, ''),
    phoneNumber: shippingAddress?.phone?.value.replace(/,/g, ''),
  };

  const totalShippingAmount = shippingSummary
    ? shippingSummary.insurance +
      shippingSummary.handlingProcessingFee +
      shippingSummary.tankRentalFee +
      shippingSummary.shippingOutBoundCost +
      shippingSummary.shippingReturnCost
    : 0;

  const handleCancelClick = () => {
    setIsEditBuyerInfoMode(false);
    setCustomerName(customerInfo.customerName);
    setCustomerEmail(customerInfo.customerEmail);
    setCustomerPhoneNumber(customerInfo.customerPhoneNumber);
  };

  const updateCustomerInfo = () => {
    setCustomerInfo({ customerName, customerEmail, customerPhoneNumber });
    setIsEditBuyerInfoMode(false);
  };

  const handleProceedToPayment = async () => {
    setIsPaymentPage(true);
  };

  const handleSubmit = async () => {
    if (!withdrawingInventory) return;

    const missingStrawItems = (withdrawCartItem?.specimenCartItems ?? []).filter(item => {
      const isFemale = item?.animal?.gender?.toLowerCase() === 'female';
      return isFemale && (item?.specimen?.embryoStraws?.length ?? 0) === 0;
    });
    if (missingStrawItems.length > 0) {
      showToast.error(toastMessages.EMBRYO_CART_MISSING_STRAW_DETAILS);
      return;
    }

    setWithdrawingInventory(false);

    const specimensSelected = Array.from(
      new Set(withdrawCartItem?.specimenCartItems.map((specimen: SpecimenCartItem) => specimen.animalId)),
    ).map((animalId: number) => {
      const itemsForAnimal = withdrawCartItem?.specimenCartItems.filter(
        (specimen: SpecimenCartItem) => specimen.animalId === animalId,
      );
      const specimenMap = new Map();
      itemsForAnimal?.forEach((specimen: SpecimenCartItem) => {
        const key = specimen.specimenId;

        if (!specimenMap.has(key)) {
          specimenMap.set(key, {
            specimenId: specimen.specimenId,
            quantity: 0,
            selectedEmbryoStrawIds: [],
          });
        }
        const entry = specimenMap.get(key);

        entry.quantity += Number(specimen.quantity) || 0;

        if (specimen.specimen?.embryoStraws) {
          specimen.specimen.embryoStraws.forEach((straw: EmbryoStraw) => {
            if (straw.embryoStrawId) {
              entry.selectedEmbryoStrawIds.push(straw.embryoStrawId);
            }
          });
        }
      });

      const specimens = Array.from(specimenMap.values()).map((specimen: SpecimenCartItem) => {
        const selectedStraws = specimen.selectedEmbryoStrawIds ?? [];
        if (selectedStraws.length === 0) {
          delete specimen.selectedEmbryoStrawIds;
        }
        return specimen;
      });

      return { specimens };
    });

    const withdrawRequestBody = {
      animalSelections: specimensSelected as { specimens: (SpecimenSelection | undefined)[] }[],
      breedDate: breedDate!,
      notes:
        notes +
        '\nBreed Date: ' +
        breedDate?.toLocaleDateString('en-US') +
        ('\nRent a dry vapor shipper tank from RSG for shipping order: ' + rentDryVaporShipperTank) +
        getShippingNotes(shippingAddress),
    };

    try {
      const response = await withdrawInventory(withdrawRequestBody, accountId!, true);
      const bulkOrderId = response?.data;
      await addBulkOrderShippingAddress({ ...bulkOrderShippingAddress, bulkOrderId: bulkOrderId }!);
      showToast.success(toastMessages.WITHDRAW_SUBMITTED);
      await deleteTransactionCartItem(user?.userId, Number(accountId), TransactionTypeEnum.Withdraw);
      handleTransactionCart();
      navigate(ROUTE_PATHS.APP_TRANSACTION_CART);
    } catch {
      showToast.error(toastMessages.WITHDRAW_SUBMIT_FAIL);
    } finally {
      setWithdrawingInventory(true);
    }
  };

  const handleTransactionCart = () => {
    permissionService.userHasRSGAdminPermission()
      ? fetchTransactionCartItems(user.userId)
      : fetchTransactionCartItems(user.userId, Number(accountId));
  };

  return (
    <div>
      <div className="step-counter">
        <div className={!isPaymentPage ? 'step-bubble active' : 'step-bubble'}>1</div>
        <div className="step-connector"></div>
        <div className={isPaymentPage ? 'step-bubble active' : 'step-bubble'}>2</div>
      </div>
      {!isPaymentPage ? (
        <div className="withdraw-shipping-preview">
          <div className="shipping-preview-header">
            <h2 className="review-purchase-title">{LABEL_CONSTANTS.REVIEW_PURCHASE_INFO}</h2>
            <label>{INVENTORY_MANAGEMENT_CONSTANTS.REVIEW_PURCHASE_INFO_SUBHEADER}</label>
          </div>
          <div className="animal-info">
            <h3 className="info-header">
              {ANIMAL_CONSTANTS.ANIMAL_INFO}
              <div className="button icon" onClick={() => setIsPreview(false)}>
                <EditSVG />
              </div>
            </h3>
            <div className="desk-select-specimen">
              <div className="specimen-table">
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                      <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}</th>
                      <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                      <th>{TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawCartItem?.specimenCartItems?.flatMap((specimenCartItem, index) => {
                      const isFemale = specimenCartItem.animal?.gender?.toLowerCase() === 'female';
                      const embryoStraws = specimenCartItem.specimen?.embryoStraws ?? [];

                      if (isFemale && embryoStraws.length > 0) {
                        return embryoStraws.map(straw => (
                          <tr key={`straw-${specimenCartItem.specimenId}-${straw.embryoStrawId}`}>
                            <td>
                              {specimenCartItem.animal?.code} - {specimenCartItem.animal?.name}
                            </td>
                            <td>{specimenCartItem.specimen.specimenType?.name}</td>
                            <td>{`${new Date(specimenCartItem.specimen.freezeDate).toLocaleDateString()} - ${
                              specimenCartItem.specimen.specimenId
                            }`}</td>
                            <td>{straw.embryoStrawCode ?? '-'}</td>
                            <td>{straw.embryoStrawNumber ?? '-'}</td>
                            <td>{straw.embryoCountPerStraw ?? '-'}</td>
                            <td>{straw.embryoCountPerStraw ?? 0}</td>
                          </tr>
                        ));
                      }

                      return (
                        <tr key={`specimen-${specimenCartItem.specimenId}-${index}`}>
                          <td>
                            {specimenCartItem.animal?.code} - {specimenCartItem.animal?.name}
                          </td>
                          <td>{specimenCartItem.specimen.specimenType?.name}</td>
                          <td>
                            {`${new Date(specimenCartItem.specimen.freezeDate).toLocaleDateString()} - ${
                              specimenCartItem.specimen.specimenId
                            }`}
                          </td>
                          <td>-</td>
                          <td>-</td>
                          <td>{specimenCartItem.availableQuantity}</td>
                          <td>{specimenCartItem.quantity}</td>
                        </tr>
                      );
                    })}

                    <tr>
                      <td colSpan={6}>{LABEL_CONSTANTS.TOTAL_QUANTITY}:</td>
                      <td>
                        <input type="text" disabled value={totalDiscardQuantity} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/** Mobile View */}
            <div className="xs-select-specimens">
              {withdrawCartItem?.specimenCartItems?.flatMap((specimenCartItem, index) => {
                const isFemale = specimenCartItem.animal?.gender?.toLowerCase() === 'female';
                const embryoStraws = specimenCartItem.specimen?.embryoStraws ?? [];

                if (isFemale && embryoStraws.length > 0) {
                  return embryoStraws.map(straw => (
                    <div className={`card max-width`} key={`straw-${specimenCartItem.specimenId}-${straw.embryoStrawId}`}>
                      <div className="selected-specimens-row">
                        <div className="select-specimens">
                          <div className="card-content-section">
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                              <label>{`${specimenCartItem.animal?.code} - ${specimenCartItem.animal?.name}`}</label>
                            </div>
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                              <label>{`${specimenCartItem.specimen.specimenType?.name}`}</label>
                            </div>
                            <div className="right column">
                              <label>{`${TABLE_HEADER_CONSTANTS.LOT_DATE_NO}: `}</label>
                              <label>{`${new Date(specimenCartItem.specimen.freezeDate).toLocaleDateString()} - ${
                                specimenCartItem.specimen.specimenId
                              }`}</label>
                            </div>
                          </div>
                          <div className="card-content-section">
                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                              <label>{straw.embryoStrawCode ?? '-'}</label>
                            </div>

                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                              <label>{straw.embryoStrawNumber ?? '-'}</label>
                            </div>

                            <div className="left column">
                              <label>{`${TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}</label>
                              <label>{straw.embryoCountPerStraw ?? '-'}</label>
                            </div>
                            <div className="right column">
                              <label>{`${TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW}: `}</label>
                              <label>{straw.embryoCountPerStraw ?? 0}</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ));
                }
                return (
                  <div className="card max-width" key={`specimen-${specimenCartItem.specimenId}-${index}`}>
                    <div className="selected-specimens-row">
                      <div className="select-specimens">
                        <div className="card-content-section">
                          <div className="left column">
                            <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                            <label>{`${specimenCartItem.animal?.code} - ${specimenCartItem.animal?.name}`}</label>
                          </div>

                          <div className="left column">
                            <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                            <label>{specimenCartItem.specimen.specimenType?.name}</label>
                          </div>

                          <div className="right column">
                            <label>{`${TABLE_HEADER_CONSTANTS.LOT_DATE_NO}: `}</label>
                            <label>
                              {`${new Date(specimenCartItem.specimen.freezeDate).toLocaleDateString()} - ${
                                specimenCartItem.specimen.specimenId
                              }`}
                            </label>
                          </div>
                        </div>

                        <div className="card-content-section">
                          <div className="left column">
                            <label>{`${TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}</label>
                            <label>{specimenCartItem.availableQuantity}</label>
                          </div>

                          <div className="right column">
                            <label>{`${TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW}: `}</label>
                            <label>{specimenCartItem.quantity}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="total-quantity">
                <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp;</label>
                {totalDiscardQuantity}
              </div>
            </div>
          </div>
          <div className="buyer-info">
            <h3 className="info-header">
              {LABEL_CONSTANTS.BUYER_INFO}
              <div className="button icon" onClick={() => setIsEditBuyerInfoMode(true)}>
                <EditSVG />
              </div>
            </h3>
            <TransactionLabelField
              label={`${LABEL_CONSTANTS.SELLER_NAME}:`}
              value={
                isBuyerInfoEditMode ? (
                  <ValidatedInput
                    useId="seller-name"
                    label={''}
                    type="text"
                    setValidatedState={event => {
                      setCustomerName({
                        value: event.value.replace(/\s+/g, ' '),
                        valid: event.valid && event.value.trim().length > 0,
                      });
                    }}
                    validators={[Validators.REQUIRED]}
                    validatedStateForAutoFill={customerName}
                  />
                ) : (
                  <label>{customerName.value || '--'}</label>
                )
              }
            />
            <TransactionLabelField
              label={`${LABEL_CONSTANTS.SELLER_EMAIL}:`}
              value={
                isBuyerInfoEditMode ? (
                  <ValidatedInput
                    useId="seller-email"
                    label={''}
                    type="email"
                    setValidatedState={event => {
                      setCustomerEmail({
                        value: event.value.replace(/\s+/g, ' '),
                        valid: event.valid && event.value.trim().length > 0,
                      });
                    }}
                    validators={[Validators.REQUIRED, Validators.EMAIL]}
                    validatedStateForAutoFill={customerEmail}
                  />
                ) : (
                  <label>{customerEmail?.value || '--'}</label>
                )
              }
            />
            <TransactionLabelField
              label={`${LABEL_CONSTANTS.SELLER_PHONE_NUMBER}:`}
              value={
                isBuyerInfoEditMode ? (
                  <ValidatedInput
                    useId="seller-phone"
                    label={''}
                    type="phone"
                    setValidatedState={setCustomerPhoneNumber}
                    validators={[Validators.REQUIRED, Validators.PHONE_LENGTH]}
                    validatedStateForAutoFill={customerPhoneNumber}
                  />
                ) : (
                  <label>{customerPhoneNumber?.value || '--'}</label>
                )
              }
            />
          </div>
          {!withdrawingInventory && <DotLoader />}
          <div className="shipping-info">
            <h3 className="info-header">
              {LABEL_CONSTANTS.SHIPPING_INFO}
              <div className="button icon" onClick={() => setIsPreview(false)}>
                <EditSVG />
              </div>
            </h3>
            <TransactionLabelField
              label={`${LABEL_CONSTANTS.WITHDRAW_METHOD}:`}
              value={<label>{LABEL_CONSTANTS.SHIP_TO_ADDRESS || '--'}</label>}
            />
            <TransactionLabelField
              label={`${ADDRESS_CONSTANTS.ADDRESS_LINE_1}:`}
              value={<label>{shippingAddress?.streetAddress.value || '--'}</label>}
            />
            <TransactionLabelField
              label={`${ADDRESS_CONSTANTS.ADDRESS_LINE_2}:`}
              value={<label>{shippingAddress?.streetAddress2.value || '--'}</label>}
            />
            <TransactionLabelField
              label={`${ADDRESS_CONSTANTS.CITY}:`}
              value={<label>{shippingAddress?.city.value || '--'}</label>}
            />
            <TransactionLabelField
              label={`${ADDRESS_CONSTANTS.STATE}:`}
              value={<label>{shippingAddress?.state.value || '--'}</label>}
            />
            <TransactionLabelField
              label={`${ADDRESS_CONSTANTS.ZIP_CODE}:`}
              value={<label>{shippingAddress?.postalCode.value || '--'}</label>}
            />
            <TransactionLabelField
              label={`${ADDRESS_CONSTANTS.COUNTRY}:`}
              value={<label>{shippingAddress?.country.value || '--'}</label>}
            />
            <TransactionLabelField
              label={`${LABEL_CONSTANTS.SHIPPING_INSURANCE}:`}
              value={<label>{shippingSummary?.shippingInsurance || '--'}</label>}
            />
            <TransactionLabelField
              label={`${PAYMENT_CONSTANTS.INSURANCE_AMOUNT}:`}
              value={<label>{`$${parseFloat(shippingSummary?.insuranceAmount?.value || '0').toFixed(2)}`}</label>}
            />
            <TransactionLabelField
              label={`${LABEL_CONSTANTS.TERMS_AND_CONDITIONS}:`}
              value={<label>{shippingSummary?.termsAndConditions || '--'}</label>}
            />
          </div>
          <div className="order-summary">
            <h3>{LABEL_CONSTANTS.ORDER_SUMMARY}</h3>
            <TransactionLabelField
              label={`${PAYMENT_CONSTANTS.SHIPPING_COST}:`}
              value={
                <label>{`$${((shippingSummary?.shippingOutBoundCost || 0) + (shippingSummary?.shippingReturnCost || 0)).toFixed(
                  2,
                )}`}</label>
              }
            />
            <TransactionLabelField
              label={`${PAYMENT_CONSTANTS.HANDLING_PROCESSING}:`}
              value={<label>{`$${shippingSummary?.handlingProcessingFee.toFixed(2)}`}</label>}
            />
            <TransactionLabelField
              label={`${PAYMENT_CONSTANTS.TANK_RENTAL_FEE}:`}
              value={<label>{`$${shippingSummary?.tankRentalFee.toFixed(2)}`}</label>}
            />
            <TransactionLabelField
              label={`${PAYMENT_CONSTANTS.INSURANCE}:`}
              value={<label>{`$${shippingSummary?.insurance.toFixed(2)}`}</label>}
            />
            <TransactionLabelField
              label={`${PAYMENT_CONSTANTS.TOTAL_AMOUNT}:`}
              value={
                <label>
                  <strong>{`$${totalShippingAmount.toFixed(2)}`}</strong>
                </label>
              }
            />
          </div>
          <div className="flex-right shipping-preview-buttons">
            {isBuyerInfoEditMode ? (
              <div className="buttons">
                <button type="button" className={'button green small'} onClick={() => handleCancelClick()}>
                  {BUTTON_CONSTANTS.CANCEL}
                </button>
                <button
                  type="button"
                  className={'button green small'}
                  onClick={() => updateCustomerInfo()}
                  disabled={!(customerName.valid && customerEmail.valid && customerPhoneNumber.valid)}>
                  {BUTTON_CONSTANTS.SAVE}
                </button>
              </div>
            ) : (
              <div className="buttons">
                <button type="button" className={'button green small'} onClick={() => setIsPreview(false)}>
                  {BUTTON_CONSTANTS.BACK}
                </button>
                <button
                  type="button"
                  className={`button green ${totalShippingAmount > 0 ? 'large' : 'small'}`}
                  onClick={totalShippingAmount > 0 ? handleProceedToPayment : handleSubmit}>
                  {totalShippingAmount > 0 ? BUTTON_CONSTANTS.PROCEED_TO_PAYMENT : BUTTON_CONSTANTS.SUBMIT}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <PaymentInformation
          setIsPaymentPage={setIsPaymentPage}
          shippingSummary={shippingSummary}
          totalShippingAmount={totalShippingAmount}
          withdrawCartItem={withdrawCartItem}
          breedDate={breedDate}
          rentDryVaporShipperTank={rentDryVaporShipperTank}
          shippingAddress={shippingAddress}
          notes={notes}
          permissionService={permissionService}
        />
      )}
    </div>
  );
};

export default ShippingPreview;
